import { useEffect, useMemo, useRef, useState } from 'react'
import * as BABYLON from '@babylonjs/core'
import { StudioArt } from '../types'
import { getGalleryCeilMeshes, getGalleryFloorMeshes, getGalleryPrintMeshes, getGalleryWallMeshes } from '@/features/gallery-wizard/galleryMeshCatalog'
import { DEFAULT_CLASSIC_FRAME_ID, DEFAULT_CLASSIC_MAT_W, getClassicFrameOption, getClassicVariant } from '../classicCatalog'
import { artForClassicSlot } from '../utils/sceneState'

const ASSET_ROOT = '/assets/patterns/'

/**
 * World-unit Y separation, from the slot mesh's own baked Y, between the
 * classic print stack's coplanar-ish layers: mat (slot mesh itself, +0) ->
 * photo inset (`PHOTO_LIFT`) -> frame border (`FRAME_LIFT`). These used to
 * be epsilon-thin (0.005 / 0.01) and leaned on material `zOffset` alone to
 * keep the stack from z-fighting — that works fine near whatever zoom the
 * `zOffset` bias was tuned at, but depth-buffer precision at a given
 * world-space gap shifts with distance from camera, so the same thin gap
 * that reads fine at one radius starts flickering once the user zooms past
 * it (worse on Classic's near-top-down orbit, staring almost straight down
 * this exact Y axis). The legacy pattern editor's own frame meshes never
 * had this problem at any zoom because their baked mitre/bevel geometry
 * gives them *real* relief above the print plane (see `Frame_008.babylon`'s
 * own ~0.044-unit Y span) — actual world-space separation, not a depth-test
 * tiebreaker, so it can't blur away as the camera moves. `FRAME_LIFT` here
 * plays the same role for the flat mat/photo pair (which has no such
 * built-in relief) and clears every catalog frame style's own worst-case
 * bevel dip (`Frame_007`'s -0.0172, the deepest) above `PHOTO_LIFT` with
 * real margin, instead of an amount so small it's back at the mercy of
 * depth-buffer precision. Still paired with `zOffset`/`zOffsetUnits` below
 * as a second line of defense — real separation is the actual fix, since it
 * turns out `zOffset` alone was barely contributing one: Babylon's
 * `zOffset` is glPolygonOffset's *factor* term, scaled by the surface's own
 * depth slope in screen space — for a flat, camera-facing plane (exactly
 * what every layer in this stack is, viewed near-top-down) that slope is
 * ~0, so `zOffset` biases almost nothing regardless of its value. The
 * *unit* term, `zOffsetUnits`, is the one that's slope-independent — set
 * below alongside `zOffset` so this stack finally gets a real fixed bias
 * instead of one that was quietly a no-op for this exact geometry.
 */
const PHOTO_LIFT = 0.02
const FRAME_LIFT = 0.05

export interface ClassicFraming {
  target: BABYLON.Vector3
  radius: number
  radiusMax: number
  /**
   * Upper bound on how far this room's locked-alpha orbit is allowed to
   * tilt off top-down (see `useStudioCameraRig`'s `CLASSIC_BETA*`
   * constants) — those are one-size-fits-all defaults tuned for a roughly
   * square room footprint. A wide-but-shallow room (big `spanX`, tiny
   * `spanZ` — several Classic templates are literally a strip of prints
   * a few units deep) needs a much bigger `radius` to fit its width in
   * frame, and with alpha locked at π/2 that same `radius` also scales
   * *any* beta tilt straight into a Z-offset (`radius * sin(beta)`) —
   * past a shallow room's own depth, that Z-offset flies the camera clean
   * through whichever authored "wall" mesh sits at the room's near Z
   * boundary, so every ray toward the target crosses solid geometry
   * first ("camera under the floor", but from crossing a wall instead of
   * the floor). Capped so the worst case — beta at its max, radius at
   * `radiusMax` (full zoom-out) — still lands inside the room's real depth.
   */
  betaCap: number
  /**
   * World-space X bounds of this room's loaded geometry — with alpha
   * locked at π/2 (see above), X is the only axis `target` can usefully
   * move along (mirrors the legacy pattern editor's own
   * `SetCameraHorizontalPosition`, which panned this exact same axis).
   * `useStudioCameraRig`'s `StudioOrbitSlider` wiring maps its 0-100%
   * onto this range instead of sweeping `alpha` like it does for a
   * procedural room. Equal to `target.x` (±0) for a degenerate/
   * single-plane load with no real width to pan across.
   */
  panMinX: number
  panMaxX: number
}

export interface UseStudioLegacyRoomApi {
  loading: boolean
  error: string | null
  /** Camera target/radius derived from this room's authored bounds + the template's saved radius/radiusMax — see useStudioCameraRig's `lockOrbitFraming`. */
  framing: ClassicFraming | null
  slotCount: number
}

/**
 * The catalog's print-slot names carry a `template_` prefix — baked on by
 * the legacy renderer's `RH.ProcessMeshes(meshes, "template", ...)` post-load
 * rename pass (src/pages/pattern/renderer/renderHelpers.js). This hook loads
 * the raw `.babylon` file straight into the scene without that rename step,
 * so slot meshes still have their on-disk names — strip the prefix back off
 * before looking them up.
 */
function rawSlotName(catalogName: string): string {
  return catalogName.replace(/^template_/, '')
}

/**
 * Loads one of the 14 original "Classic" gallery room shapes — a legacy
 * `.babylon` file — directly into the live studio scene, and keeps its
 * pre-baked print-slot meshes textured with whatever photos are currently
 * in `arts` (matched by `classicSlotIndex` when set, else array order —
 * slot 0 gets that art; extras beyond the room's slot count aren't shown).
 * Slots beyond however many real prints exist get padded with an inert
 * placeholder `StudioArt` by `useStudioArts`' `padClassicSlots` (see
 * Studio3DScreen), so every slot still ends up `art`-backed here and picks
 * up `metadata.studioArtId` — without that, those meshes would have no id
 * for `useStudioTools`' click-to-select to resolve, and could never be
 * selected to hang a first photo into.
 *
 * Mutually exclusive with `useStudioRoom`'s procedural shell (gated on
 * `room.mode` there) — this hook owns the scene entirely while active.
 * Unlike `useStudioArts`, there's no per-print spotlight mesh, and no
 * separate mat mesh either — the slot mesh's own geometry is just one photo
 * plane. Frame *is* real here too (see `applyFrames` below): each print's
 * own `classicFrameId` picks a real frame model (`Assets.frames`), grouped by
 * frame choice so identical choices across slots share one loaded model
 * (many instances). Mat is faked on top of that fixed geometry instead (see
 * `applyPrintTextures`): the slot mesh itself becomes the mat-colored
 * background, and a smaller plane holding the actual photo texture is
 * inset within it by `classicMatW`.
 */
export function useStudioLegacyRoom(
  scene: BABYLON.Scene | null,
  templateId: number | undefined,
  wallColor: string,
  floorColor: string,
  ceilColor: string | undefined,
  arts: StudioArt[],
  selectedId?: string | null,
): UseStudioLegacyRoomApi {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [framing, setFraming] = useState<ClassicFraming | null>(null)
  const [slotCount, setSlotCount] = useState(0)

  const containerRef = useRef<BABYLON.AssetContainer | null>(null)
  const printMatsRef = useRef<BABYLON.StandardMaterial[]>([])
  const printTexRef = useRef<BABYLON.Texture[]>([])
  /** Inset photo planes created when a slot's `classicMatW` > 0 — see `applyPrintTextures`. */
  const matInsetMeshesRef = useRef<BABYLON.Mesh[]>([])
  const highlightRef = useRef<BABYLON.HighlightLayer | null>(null)
  const frameMeshesRef = useRef<BABYLON.AbstractMesh[]>([])
  const frameGenRef = useRef(0)
  // Skips a full dispose+reload of every frame instance when `arts` changes
  // for an unrelated reason (rename keystrokes, reorders, etc) — only the
  // per-slot resolved frame ids actually matter here.
  const frameSignatureRef = useRef('')
  const wallColorRef = useRef(wallColor)
  wallColorRef.current = wallColor
  const floorColorRef = useRef(floorColor)
  floorColorRef.current = floorColor
  // Falls back to the wall tone — these rooms predate a dedicated ceiling
  // control, so older saves have no `ceilColor` of their own yet.
  const ceilColorRef = useRef(ceilColor)
  ceilColorRef.current = ceilColor
  const artsRef = useRef(arts)
  artsRef.current = arts

  const disposeFrameMeshes = () => {
    // Instances first (they don't own the material/geometry), then the
    // source meshes, then materials — otherwise disposing a source's
    // material inline (as `m.dispose(false, true)` used to) could pull it
    // out from under a sibling source mesh still being disposed in the same
    // pass: a 9-sliced frame's 8 parts all share one material instance, so
    // materials are collected and disposed exactly once here instead.
    const [instances, rest] = [
      frameMeshesRef.current.filter((m): m is BABYLON.InstancedMesh => m instanceof BABYLON.InstancedMesh),
      frameMeshesRef.current.filter((m) => !(m instanceof BABYLON.InstancedMesh)),
    ]
    instances.forEach((m) => m.dispose())
    const materials = new Set(rest.map((m) => m.material).filter((mat): mat is BABYLON.Material => !!mat))
    rest.forEach((m) => m.dispose(false, false))
    materials.forEach((mat) => mat.dispose())
    frameMeshesRef.current = []
  }

  /**
   * Per-slot layout for the 9-sliced `patternMeshPath` frame parts — adapts
   * `applyPatternFrame`/`adjustPatternFrameScale` (src/pages/pattern/renderer/main.js):
   * corner pieces are only ever repositioned to the slot's four corners,
   * never scaled non-uniformly, so their baked mitre/bevel geometry stays
   * undistorted at any aspect ratio; side pieces stretch along their own
   * length axis to span the gap between corners.
   *
   * Unlike the legacy pattern editor, though, corner/side *thickness* here
   * scales with `frameThickness` (`min(slot width, slot height)`) instead of
   * staying pinned at the model's authored scale of 1: that legacy code only
   * ever sized one fixed print canvas, always close to the model's own
   * ~1-unit footprint, so an unscaled ~0.08-unit border read as a sane ~8%
   * proportion there. Classic print slots range from a few centimeters to
   * multiple meters across the 14 rooms — leaving thickness at 1 made the
   * border relatively enormous (ballooning to cover most of a small slot)
   * or vanishingly thin (near-invisible on a large one), instead of a
   * consistent proportion of the slot either way. `Frame_Back` is dropped —
   * it's a backing plate for the pattern editor's own frame+mat stack,
   * which Classic slots don't need since the room's baked slot mesh already
   * serves as that background.
   */
  const FRAME_PART_LAYOUT: Record<string, { x: 'min' | 'max' | 'center'; z: 'min' | 'max' | 'center'; lengthAxis?: 'x' | 'z' }> = {
    Frame_CornerBL: { x: 'max', z: 'max' },
    Frame_CornerBR: { x: 'min', z: 'max' },
    Frame_CornerTL: { x: 'max', z: 'min' },
    Frame_CornerTR: { x: 'min', z: 'min' },
    Frame_SideB: { x: 'center', z: 'max', lengthAxis: 'x' },
    Frame_SideT: { x: 'center', z: 'min', lengthAxis: 'x' },
    Frame_SideL: { x: 'max', z: 'center', lengthAxis: 'z' },
    Frame_SideR: { x: 'min', z: 'center', lengthAxis: 'z' },
  }

  /**
   * Mirrors `applyGalleryFrame`/`adjustFrameScale` (src/pages/pattern/renderer/main.js)
   * per print instead of topic-wide, but swaps in the 9-sliced
   * `patternMeshPath` model (see `ClassicFrameOption`) instead of the legacy
   * single-mesh one whenever it's available — every Classic frame style
   * currently has one. Each of its 8 border parts gets an instance per slot
   * that picked it, positioned/scaled per `FRAME_PART_LAYOUT` against that
   * slot's own bounding box. Slots are grouped by frame choice first so N
   * slots sharing a style only load that model once.
   */
  const applyFrames = (scn: BABYLON.Scene, id: number) => {
    const slots = getGalleryPrintMeshes(id)
    const slotFrameIds = slots.map((_, i) => {
      const art = artForClassicSlot(artsRef.current, i)
      // Hidden (task #21) — no frame floating over a print that isn't
      // there. `0` is the same "no frame" sentinel the grouping loop below
      // already skips for a slot with no `fId`.
      if (art?.hidden) return 0
      return art?.classicFrameId ?? DEFAULT_CLASSIC_FRAME_ID ?? 0
    })
    const signature = slotFrameIds.join(',')
    if (signature === frameSignatureRef.current) return
    frameSignatureRef.current = signature

    const gen = ++frameGenRef.current
    disposeFrameMeshes()

    const bySlotIndices = new Map<number, number[]>()
    slotFrameIds.forEach((fId, i) => {
      if (!fId) return
      const list = bySlotIndices.get(fId)
      if (list) list.push(i)
      else bySlotIndices.set(fId, [i])
    })

    const loadFrame = (relativePath: string) => {
      const fullPath = `${ASSET_ROOT}${relativePath}`
      const lastSlash = fullPath.lastIndexOf('/')
      const rootUrl = fullPath.slice(0, lastSlash + 1)
      const fileName = fullPath.slice(lastSlash + 1)
      return BABYLON.SceneLoader.ImportMeshAsync(null, rootUrl, fileName, scn)
    }

    const slotBox = (i: number) => {
      const slotMesh = scn.getMeshByName(rawSlotName(slots[i]))
      if (!slotMesh) return null
      const bb = slotMesh.getBoundingInfo().boundingBox
      return {
        slotMesh,
        min: bb.minimumWorld.x,
        max: bb.maximumWorld.x,
        minZ: bb.minimumWorld.z,
        maxZ: bb.maximumWorld.z,
        centerX: (bb.maximumWorld.x + bb.minimumWorld.x) / 2,
        centerZ: (bb.maximumWorld.z + bb.minimumWorld.z) / 2,
        width: bb.maximumWorld.x - bb.minimumWorld.x,
        height: bb.maximumWorld.z - bb.minimumWorld.z,
      }
    }

    bySlotIndices.forEach((slotIndices, fId) => {
      const option = getClassicFrameOption(fId)
      if (!option) return

      if (!option.patternMeshPath) {
        // Fallback for a frame style with no 9-sliced asset (none exist
        // today, but stay honest if the catalog ever gains one) — same
        // whole-mesh non-uniform stretch the legacy renderer itself used,
        // corner distortion on non-square slots included.
        loadFrame(option.meshPath)
          .then(({ meshes }) => {
            const source = meshes[0] as BABYLON.Mesh | undefined
            if (!source) return
            if (frameGenRef.current !== gen || !containerRef.current) {
              meshes.forEach((m) => m.dispose(false, true))
              return
            }
            source.isPickable = false
            source.setEnabled(true)
            if (source.material) {
              source.material.zOffset = -4
              source.material.zOffsetUnits = -8
            }

            slotIndices.forEach((i, idx) => {
              const box = slotBox(i)
              if (!box) return
              const inst: BABYLON.AbstractMesh = idx === 0 ? source : source.createInstance(`classic-frame-${fId}-${i}`)
              inst.scaling.x = box.width
              inst.scaling.z = box.height
              inst.position.x = box.centerX
              inst.position.z = box.centerZ
              inst.position.y = box.slotMesh.position.y + FRAME_LIFT
              inst.isPickable = true
              inst.metadata = { ...(inst.metadata || {}), studioArtId: artForClassicSlot(artsRef.current, i)?.id }
              frameMeshesRef.current.push(inst)
            })

            meshes.slice(1).forEach((m) => m.dispose(false, true))
          })
          .catch((err) => {
            // eslint-disable-next-line no-console
            console.error('[gallery-studio-3d] failed to load classic frame', err)
          })
        return
      }

      loadFrame(option.patternMeshPath)
        .then(({ meshes }) => {
          if (frameGenRef.current !== gen || !containerRef.current) {
            meshes.forEach((m) => m.dispose(false, true))
            return
          }

          const parts = new Map<string, BABYLON.Mesh>()
          meshes.forEach((m) => {
            if (m.name === 'Frame_Back' || !FRAME_PART_LAYOUT[m.name]) {
              // All 9 parts in these files share one material instance —
              // disposing it here (disposeMaterialAndTextures=true) would
              // pull the texture/albedo out from under every side/corner
              // mesh we're about to keep and use, leaving them rendering
              // with Babylon's fallback white material. Drop the geometry
              // only; the shared material is disposed once for real by
              // `disposeFrameMeshes` when this frame group is later torn down.
              m.dispose(false, false)
              return
            }
            const mesh = m as BABYLON.Mesh
            mesh.isPickable = false
            mesh.setEnabled(true)
            // Same z-fighting risk as the mat/photo pair below (frame border
            // overhangs right at the photo's edge) — bias it in front so the
            // seam doesn't flicker as the camera zooms either.
            if (mesh.material) {
              mesh.material.zOffset = -4
              mesh.material.zOffsetUnits = -8
            }
            parts.set(m.name, mesh)
          })

          slotIndices.forEach((i, idx) => {
            const box = slotBox(i)
            if (!box) return
            const studioArtId = artForClassicSlot(artsRef.current, i)?.id
            // Uniform on both axes (never `width` on one, `height` on the
            // other) so corner scaling can't skew their 45° mitres — see
            // `FRAME_PART_LAYOUT` doc comment above.
            const frameThickness = Math.min(box.width, box.height)

            parts.forEach((source, partName) => {
              const layout = FRAME_PART_LAYOUT[partName]
              const inst: BABYLON.AbstractMesh = idx === 0 ? source : source.createInstance(`classic-frame-${fId}-${partName}-${i}`)
              inst.position.x = layout.x === 'min' ? box.min : layout.x === 'max' ? box.max : box.centerX
              inst.position.z = layout.z === 'min' ? box.minZ : layout.z === 'max' ? box.maxZ : box.centerZ
              inst.position.y = box.slotMesh.position.y + FRAME_LIFT
              if (layout.lengthAxis === 'x') {
                inst.scaling.x = box.width
                inst.scaling.z = frameThickness
              } else if (layout.lengthAxis === 'z') {
                inst.scaling.z = box.height
                inst.scaling.x = frameThickness
              } else {
                // Corner — same factor on both axes keeps its baked mitre at 45°.
                inst.scaling.x = frameThickness
                inst.scaling.z = frameThickness
              }
              // The frame border sits visually on top of (and covers most
              // of) its print — without its own `studioArtId`, clicking any
              // part of it (rather than the sliver of bare mat/photo it
              // borders) would pick a mesh with no id and silently do nothing.
              inst.isPickable = true
              inst.metadata = { ...(inst.metadata || {}), studioArtId }
              frameMeshesRef.current.push(inst)
            })
          })
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[gallery-studio-3d] failed to load classic frame', err)
        })
    })
  }

  const disposePrintMats = () => {
    printMatsRef.current.forEach((m) => m.dispose())
    printMatsRef.current = []
    printTexRef.current.forEach((t) => t.dispose())
    printTexRef.current = []
    matInsetMeshesRef.current.forEach((m) => m.dispose(false, true))
    matInsetMeshesRef.current = []
  }

  /**
   * Classic rooms' wall/floor/ceiling materials are all loaded straight
   * off their legacy .babylon files as `PBRMaterial`s, and — save for a
   * handful of flat white ones (Classic 03/06/10's walls, most ceilings) —
   * carry a real scanned photo texture (plaster/stucco/concrete/brick/
   * wood/marble/carpet). Babylon's PBR shader renders
   * `albedoColor × albedoTexture`, a straight multiply, so just setting
   * `albedoColor` (as this used to) leaves that texture's own shading
   * fully in play. That's *wanted* on something like a wood floor — the
   * grain's own light/dark variation still reads through the tint, so a
   * light pick looks like lighter wood and a dark pick like darker wood —
   * but it breaks down wherever the baked texture itself is dark (mortar
   * lines, shadowed plaster creases, ambient occlusion baked into the
   * scan): multiplying a bright, saturated pick against a near-black
   * texture pixel there crushes straight to black, which is what made
   * Classic 09's all-plaster walls read as "basically black" under a
   * purple tint despite the swatch itself being nowhere near black.
   *
   * Fix keeps the multiply (so the texture's own shading still comes
   * through) and adds a small *emissive* floor in the same hue only when a
   * texture is present. Emissive is additive and independent of the
   * albedo×texture multiply (and of scene lighting/shadow), so a dark
   * texture pixel can't multiply it away — it just lightly lifts the
   * surface's darkest crevices toward a dim tint of the pick instead of
   * true black, while regions already well above that floor (most of any
   * texture) look effectively unchanged. Untextured materials are left
   * alone entirely — they already render the picked color exactly.
   */
  const TEXTURED_TINT_EMISSIVE_FLOOR = 0.16

  const applyTint = (mat: BABYLON.Material | null | undefined, hex: string) => {
    const color = BABYLON.Color3.FromHexString(hex)
    if (mat instanceof BABYLON.PBRMaterial) {
      mat.albedoColor = color
      if (mat.albedoTexture) mat.emissiveColor = color.scale(TEXTURED_TINT_EMISSIVE_FLOOR)
    } else if (mat instanceof BABYLON.StandardMaterial) {
      mat.diffuseColor = color
      if (mat.diffuseTexture) mat.emissiveColor = color.scale(TEXTURED_TINT_EMISSIVE_FLOOR)
    }
  }

  const applyWallColor = (scn: BABYLON.Scene, id: number) => {
    getGalleryWallMeshes(id).forEach((name) => {
      applyTint(scn.getMeshByName(name)?.material, wallColorRef.current)
    })
  }

  const applyFloorColor = (scn: BABYLON.Scene, id: number) => {
    getGalleryFloorMeshes(id).forEach((name) => {
      applyTint(scn.getMeshByName(name)?.material, floorColorRef.current)
    })
  }

  const applyCeilColor = (scn: BABYLON.Scene, id: number) => {
    const hex = ceilColorRef.current || wallColorRef.current
    getGalleryCeilMeshes(id).forEach((name) => {
      const ceil = scn.getMeshByName(name)
      if (!ceil) return
      // Classic 13's ceiling ships from its source .babylon file with no
      // material assigned at all (falls back to the scene's shared
      // default gray material, untouched by any color control) — give it
      // a real one on first tint so the ceiling swatch has something to
      // color instead of silently no-op'ing on this one family.
      if (!ceil.material) ceil.material = new BABYLON.PBRMaterial(`${name}-tint`, scn)
      applyTint(ceil.material, hex)
    })
  }

  /**
   * Textures each Classic slot with its print's photo, faking a mat border
   * when `classicMatW` > 0. The slot mesh's baked geometry can't shrink to
   * make room for a real mat gap, so instead: the slot mesh itself becomes a
   * flat mat-tone background (no texture), and a smaller `CreateGround`
   * plane — sized down by the mat fraction, same flat XZ orientation as the
   * slot — sits above it holding the actual photo texture. Both meshes (and,
   * with matW at 0, just the slot mesh) carry `studioArtId` so picking
   * either one still selects this print (see `useStudioTools`).
   */
  const applyPrintTextures = (scn: BABYLON.Scene, id: number) => {
    disposePrintMats()
    const slots = getGalleryPrintMeshes(id)
    slots.forEach((catalogName, i) => {
      const mesh = scn.getMeshByName(rawSlotName(catalogName))
      const art = artForClassicSlot(artsRef.current, i)
      if (!mesh) return
      // Owner hid this print (task #21) — there's no separate frame/mat/photo
      // mesh to disable here like Studio mode has, just this one baked slot
      // plane, so disable it directly. It's mounted flush against the wall
      // (no recess), so this reveals bare wall behind it, not a hole.
      if (art?.hidden) {
        mesh.setEnabled(false)
        return
      }
      mesh.setEnabled(true)
      if (!art) return
      mesh.metadata = { ...(mesh.metadata || {}), studioArtId: art.id }

      const matWFrac = Math.max(0, art.classicMatW ?? DEFAULT_CLASSIC_MAT_W)
      const bb = mesh.getBoundingInfo().boundingBox
      const width = bb.maximumWorld.x - bb.minimumWorld.x
      const depthZ = bb.maximumWorld.z - bb.minimumWorld.z
      // Cap the pad so the inset photo never collapses to nothing on a
      // narrow/tall slot — leaves at least 40% of the shorter side visible.
      const pad = Math.min(matWFrac * Math.min(width, depthZ), Math.min(width, depthZ) * 0.3)

      const tex = new BABYLON.Texture(art.img, scn)
      tex.anisotropicFilteringLevel = 8

      if (pad > 0.001) {
        const bgMat = new BABYLON.StandardMaterial(`classic-mat-${catalogName}`, scn)
        bgMat.diffuseColor = BABYLON.Color3.FromHexString(art.matTone)
        bgMat.emissiveColor = BABYLON.Color3.FromHexString(art.matTone).scale(0.35)
        bgMat.specularColor = BABYLON.Color3.Black()
        // This slot mesh is mounted flush against the room's wall behind
        // it — barely any authored gap, same category of z-fight risk as
        // the photo/frame layers stacked on top of it, just against the
        // *wall* instead of each other (confirmed by hiding `Scene_Wall_00X`
        // in the inspector: the bleed-through on the mat disappears). Can't
        // give this one `PHOTO_LIFT`/`FRAME_LIFT`-style real separation
        // without repositioning the room's own baked mesh — not worth the
        // risk of compounding on every re-run of this function (new art,
        // new mat color, etc.) — so it leans on bias alone, one notch
        // weaker than the photo it sits under.
        bgMat.zOffset = -1
        bgMat.zOffsetUnits = -2
        mesh.material = bgMat
        printMatsRef.current.push(bgMat)

        const centerX = (bb.maximumWorld.x + bb.minimumWorld.x) / 2
        const centerZ = (bb.maximumWorld.z + bb.minimumWorld.z) / 2

        // Every 9-sliced frame part's own baked geometry sits flush against
        // (and only ever extends outward from) this slot's own edge — see
        // `FRAME_PART_LAYOUT`'s doc comment in `applyFrames` — so the frame
        // border and this mat never actually overlap, just touch. That's
        // invisible dead-on, but `FRAME_LIFT` raises the frame a real step
        // above the mat, and Classic's camera does tilt off top-down (see
        // `CLASSIC_BETA`) — at any non-zero tilt, a raised edge that only
        // touches (rather than overlaps) the surface below it can reveal a
        // sliver of whatever's *beneath* that riser right at the seam.
        //
        // A wider same-color "skirt" mesh under the frame was tried here to
        // paper over that sliver, but it's not safe: this slot mesh's own
        // true footprint is the one dimension we can't safely grow past —
        // several catalog rooms mount prints in a genuinely recessed wall
        // niche (a real 3D reveal, not just a flat decal), and *any* mesh
        // wider than the slot's own baked bounds risks poking through that
        // niche's own bevel/side wall. That reads as a hard black seam (the
        // unlit interior of the intersection) instead of the softer
        // wall-color sliver it was meant to replace — worse, and uniform
        // around the whole border regardless of tilt, rather than a subtle
        // angle-dependent reveal. Left at flush-with-`box` (no overhang)
        // until there's a per-room-safe way to know how much clearance an
        // oversized mat can actually take.
        const inset = BABYLON.MeshBuilder.CreateGround(
          `classic-photo-${catalogName}`,
          { width: width - 2 * pad, height: depthZ - 2 * pad },
          scn,
        )
        inset.position.x = centerX
        inset.position.z = centerZ
        // Sits above the mat background but still under the frame's lift — see `PHOTO_LIFT`/`FRAME_LIFT`.
        inset.position.y = mesh.position.y + PHOTO_LIFT
        inset.isPickable = true
        inset.metadata = { studioArtId: art.id }

        // `CreateGround`'s UV winding runs opposite `CreatePlane`'s (its V
        // axis tracks -Z, not the vertical axis a wall-mounted photo plane
        // uses in Studio mode) — without this flip every inset photo renders
        // upside down, since this ground-plane inset is new geometry with no
        // relation to the original mesh's own (correct) baked-in UVs.
        tex.vScale = -1
        const photoMat = new BABYLON.StandardMaterial(`classic-photo-mat-${catalogName}`, scn)
        photoMat.diffuseTexture = tex
        photoMat.emissiveTexture = tex
        photoMat.emissiveColor = BABYLON.Color3.FromHexString('#5e5e5e')
        photoMat.specularColor = BABYLON.Color3.FromHexString('#161616')
        // `PHOTO_LIFT` above is the real fix for z-fighting across zoom
        // levels (see its doc comment) — `zOffset`/`zOffsetUnits` on top is
        // just a second line of defense, biasing the depth *value* itself
        // so this still wins the depth test even in the (now much less
        // likely) case the real Y gap alone isn't enough at some extreme
        // zoom. `zOffsetUnits` (not scaled by surface slope, unlike
        // `zOffset`) is the one actually doing anything for a flat,
        // near-top-down plane like this — see the shared doc comment above.
        photoMat.zOffset = -2
        photoMat.zOffsetUnits = -4
        inset.material = photoMat
        printMatsRef.current.push(photoMat)
        matInsetMeshesRef.current.push(inset)
      } else {
        const mat = new BABYLON.StandardMaterial(`classic-print-${catalogName}`, scn)
        mat.diffuseTexture = tex
        mat.emissiveTexture = tex
        mat.emissiveColor = BABYLON.Color3.FromHexString('#5e5e5e')
        mat.specularColor = BABYLON.Color3.FromHexString('#161616')
        // Same wall-bleed risk as `bgMat` above — this mesh is both the
        // "mat" and the photo here (no separate inset when there's no mat
        // width), so it gets the stronger of the two biases.
        mat.zOffset = -2
        mat.zOffsetUnits = -4
        mesh.material = mat
        printMatsRef.current.push(mat)
      }
      printTexRef.current.push(tex)
    })
  }

  // Load (or swap) the room shell whenever the selected Classic template changes.
  useEffect(() => {
    if (!scene || !templateId) {
      setFraming(null)
      setSlotCount(0)
      return
    }
    const variant = getClassicVariant(templateId)
    if (!variant) {
      setError(`Unknown classic scene id ${templateId}`)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    const fullPath = `${ASSET_ROOT}${variant.meshPath}`
    const lastSlash = fullPath.lastIndexOf('/')
    const rootUrl = fullPath.slice(0, lastSlash + 1)
    const fileName = fullPath.slice(lastSlash + 1)

    BABYLON.SceneLoader.LoadAssetContainerAsync(rootUrl, fileName, scene)
      .then((container) => {
        if (cancelled) {
          container.dispose()
          return
        }
        container.addAllToScene()
        containerRef.current = container
        highlightRef.current = new BABYLON.HighlightLayer('studio-classic-hl', scene, { blurHorizontalSize: 0.7, blurVerticalSize: 0.7 })
        highlightRef.current.innerGlow = false
        // These files ship with `pickable: false` baked in — the legacy
        // renderer always overrode it right after load (`RH.ProcessMeshes`
        // sets `isPickable = true` on every non-Scene/Light mesh). But these
        // rooms' "floor"/"wall"/"ceiling" shell meshes are themselves the
        // flattened-for-top-down legacy geometry (see the framing comment
        // below) — often a single huge plane spanning the whole room at a
        // near-constant depth. From this room's own locked top-down camera,
        // a pick ray toward a print slot frequently crosses that giant shell
        // plane *before* reaching the (much smaller) print quad behind/below
        // it, so an indiscriminately pickable shell silently wins every
        // print click. Only the actual print slots need to be pickable here.
        const slotRawNames = new Set(getGalleryPrintMeshes(templateId).map(rawSlotName))
        container.meshes.forEach((m) => {
          m.metadata = { ...(m.metadata || {}), studioClassicMesh: true }
          m.isPickable = slotRawNames.has(m.name)
        })

        applyWallColor(scene, templateId)
        applyFloorColor(scene, templateId)
        applyCeilColor(scene, templateId)
        applyPrintTextures(scene, templateId)
        applyFrames(scene, templateId)
        setSlotCount(getGalleryPrintMeshes(templateId).length)

        // These rooms were authored for the legacy pattern editor's fixed
        // bird's-eye camera (see `createCamera`/`SetCameraHorizontalPosition`
        // in src/pages/pattern/renderer/main.js) — alpha locked at π/2, beta
        // clamped to a near-top-down sliver, target always `(panX, 0, 0)`.
        // A generic eye-level orbit target (this room's full 3D bounding-box
        // center) looks broken here: several of these files put "floor"/
        // "ceiling" meshes at matching, unusually large Y values and vary
        // instead on Z, i.e. their vertex data already encodes the
        // flattened-for-top-down look, not a real walk-in room. Only X
        // (the pan axis) is meaningful for the *target*; keep it at Y/Z 0
        // like the original always did. The X/Z footprint below still gets
        // tracked though — not to move the target, but to size the radius.
        let minX = Infinity
        let maxX = -Infinity
        let minZ = Infinity
        let maxZ = -Infinity
        container.meshes.forEach((m) => {
          if (!(m instanceof BABYLON.Mesh) || m.getTotalVertices() === 0) return
          const bb = m.getBoundingInfo().boundingBox
          minX = Math.min(minX, bb.minimumWorld.x)
          maxX = Math.max(maxX, bb.maximumWorld.x)
          minZ = Math.min(minZ, bb.minimumWorld.z)
          maxZ = Math.max(maxZ, bb.maximumWorld.z)
        })
        const targetX = Number.isFinite(minX) && Number.isFinite(maxX) ? (minX + maxX) / 2 : 0
        const spanX = Number.isFinite(minX) && Number.isFinite(maxX) ? maxX - minX : 0
        const spanZ = Number.isFinite(minZ) && Number.isFinite(maxZ) ? maxZ - minZ : 0

        // The `radiusMax` saved per variant in the catalog was authored for
        // the *old* pattern editor's own fixed camera/projection, not this
        // orbit camera's FOV — on wider rooms (more slots, more walls) that
        // legacy radiusMax leaves almost no headroom to zoom out past the
        // initial framing, so much of the room stays off-screen ("scene
        // isn't scaled out enough"). This near-top-down orbit (alpha
        // locked, beta a shallow sliver — see useStudioCameraRig) sits
        // almost directly above its target, so the camera height above the
        // floor (~= radius) times its vertical FOV sets how much ground
        // plane is visible. Solve that in reverse for the radius that would
        // fit this room's actual loaded footprint, then pad it for
        // breathing room, and only ever widen the authored `radiusMax`
        // ceiling with it — *not* the starting `radius` itself (see below).
        // The inspector panel eats a variable, often large chunk of the
        // viewport's width, so the canvas' visible aspect ratio isn't
        // reliably wide — sizing off `spanX` directly (not
        // `spanX / assumedAspect`) assumes the tighter, closer-to-square
        // case. That's conservative on genuinely wide viewports (more
        // headroom than strictly needed) but never leaves a narrower one
        // still cropped.
        const CLASSIC_FOV = 0.8
        const fitHeight = Math.max(spanX, spanZ) / (2 * Math.tan(CLASSIC_FOV / 2))
        const fitRadius = fitHeight * 1.35

        // Unlike `radiusMax` above, the *starting* radius stays exactly the
        // legacy-authored value — this used to also get widened by
        // `fitRadius`, but that number sizes the room's full loaded
        // footprint (every wall, including blank ones well off to the
        // sides), not the print-bearing face itself. On a wide room like
        // Classic 09 that inflated the opening framing to ~4x the
        // hand-tuned legacy radius (e.g. ~21 vs. the authored 5.3), which
        // read as "starts zoomed way out" — and (see useStudioCameraRig)
        // fed straight into how close you could zoom back in too, since
        // that floor is itself a fraction of this radius.
        const radius = variant.radius ?? 4
        const radiusMax = Math.max(variant.radiusMax ?? 12, fitRadius)
        // Keep the worst case (full zoom-out, max tilt) inside ~30% of the
        // room's real depth — the other 70% is margin before actually
        // reaching the wall at the boundary. `spanZ` can be ~0 for a
        // degenerate/single-plane load; floor it so `asin` never sees 0/0.
        const safeZOffset = Math.max(0.35, spanZ) * 0.3
        const betaCap = Math.max(0.02, Math.asin(Math.min(1, safeZOffset / radiusMax)))

        setFraming({
          target: new BABYLON.Vector3(targetX, 0, 0),
          radius,
          radiusMax,
          betaCap,
          panMinX: Number.isFinite(minX) ? minX : targetX,
          panMaxX: Number.isFinite(maxX) ? maxX : targetX,
        })
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        // eslint-disable-next-line no-console
        console.error('[gallery-studio-3d] failed to load classic scene', err)
        setError(err instanceof Error ? err.message : 'Failed to load classic scene')
        setLoading(false)
      })

    return () => {
      cancelled = true
      disposePrintMats()
      disposeFrameMeshes()
      frameSignatureRef.current = ''
      highlightRef.current?.dispose()
      highlightRef.current = null
      const container = containerRef.current
      if (container) {
        container.removeAllFromScene()
        container.dispose()
        containerRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, templateId])

  // Selection ring — mirrors useStudioArts' own HighlightLayer for
  // procedural prints, since classic slot meshes never get `handles` there.
  useEffect(() => {
    const hl = highlightRef.current
    if (!hl) return
    hl.removeAllMeshes()
    if (!selectedId || !scene) return
    const mesh = scene.meshes.find((m) => m.metadata?.studioArtId === selectedId)
    if (mesh) hl.addMesh(mesh as BABYLON.Mesh, BABYLON.Color3.FromHexString('#be9dfd'))
  }, [scene, selectedId, slotCount])

  // Re-texture print slots and re-apply frames whenever the photo list (or
  // any print's `classicFrameId`) changes (swap/reorder/per-print frame
  // pick/etc) — independent of the (heavier) load effect above, which only
  // re-runs on a template swap. Guarded to loaded rooms only; the load
  // effect above handles the very first pass once the file finishes parsing.
  useEffect(() => {
    if (!scene || !templateId || !containerRef.current) return
    applyPrintTextures(scene, templateId)
    applyFrames(scene, templateId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, templateId, arts])

  // Live wall/floor-color retint — the inspector's WALLS/FLOOR swatches call
  // this while a Classic room is loaded, same as they do for the procedural shell.
  useEffect(() => {
    if (!scene || !templateId || !containerRef.current) return
    applyWallColor(scene, templateId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, templateId, wallColor])

  useEffect(() => {
    if (!scene || !templateId || !containerRef.current) return
    applyFloorColor(scene, templateId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, templateId, floorColor])

  useEffect(() => {
    if (!scene || !templateId || !containerRef.current) return
    applyCeilColor(scene, templateId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, templateId, ceilColor])

  return useMemo(() => ({ loading, error, framing, slotCount }), [loading, error, framing, slotCount])
}
